/**
 * App Panel Store
 *
 * Global Zustand store for managing the side panel that opens
 * app pages inline (from the AppLauncher "Open as Panel" button).
 *
 * Uses an iframe to render the target page without navigating away.
 *
 * Features:
 * - Minimize to taskbar: Collapses panel to bottom taskbar (Windows-style)
 * - Multiple minimized panels supported with preserved iframe state
 * - Focus mode: Hides other UI elements for distraction-free work
 * - localStorage persistence for page refresh survival
 * - PostgreSQL cloud sync for cross-device persistence
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useEffect } from 'react';
import { createCloudSync } from './zustandCloudSync';

/** Panel width options */
export type PanelWidth = 'md' | 'lg' | 'xl' | 'full' | 'fullscreen';

/**
 * Active panel - a panel with a mounted iframe (visible or hidden)
 * These panels preserve their iframe state when minimized
 */
export interface ActiveAppPanel {
  /** Unique identifier */
  id: string;
  /** Route path loaded in the iframe */
  route: string;
  /** Display title */
  title: string;
  /** Panel width preference */
  width: PanelWidth;
  /** Whether this panel is currently hidden (minimized to taskbar) */
  isHidden: boolean;
  /** Timestamp when created/minimized (for ordering in taskbar) */
  createdAt: number;
}

/** Minimized panel data for persistence (subset of ActiveAppPanel) */
export interface MinimizedAppPanel {
  /** Unique identifier */
  id: string;
  /** Route path to load when restored */
  route: string;
  /** Display title */
  title: string;
  /** Saved width preference */
  width: PanelWidth;
  /** Timestamp when minimized (for ordering) */
  minimizedAt: number;
}

export interface AppPanelState {
  /** All panels with mounted iframes (visible and hidden) */
  activePanels: ActiveAppPanel[];
  /** ID of the currently visible panel (null if none visible) */
  visiblePanelId: string | null;
  /** Focus mode hides other UI elements */
  isFocusMode: boolean;
  /** Whether the taskbar is visible (computed from hidden panels count) */
  isTaskbarVisible: boolean;

  // Legacy compatibility fields
  /** Whether any panel is open (derived from visiblePanelId !== null) */
  isOpen: boolean;
  /** Current visible panel's route (for legacy compatibility) */
  route: string | null;
  /** Current visible panel's title (for legacy compatibility) */
  title: string | null;
  /** Current visible panel's width (for legacy compatibility) */
  width: PanelWidth;

  /** Open a new panel or restore existing one with same route */
  openPanel: (route: string, title: string, width?: PanelWidth) => void;
  /** Close the currently visible panel (removes iframe entirely) */
  closePanel: () => void;
  /** Set width of current visible panel */
  setWidth: (width: PanelWidth) => void;
  /** Minimize current visible panel to taskbar (preserves iframe) */
  minimizePanel: () => void;
  /** Toggle focus mode */
  toggleFocusMode: () => void;
  /** Restore a specific panel from taskbar by ID */
  restoreMinimizedPanel: (id: string) => void;
  /** Close/remove a minimized panel from taskbar (destroys iframe) */
  closeMinimizedPanel: (id: string) => void;
  /** Get all minimized (hidden) panels for taskbar display */
  getMinimizedPanels: () => MinimizedAppPanel[];

  // Legacy compatibility methods
  restorePanel: () => void;
  toggleMinimize: () => void;
  setPillPosition: (position: number) => void;
  minimizedPanels: MinimizedAppPanel[];
  isMinimized: boolean;
  pillPosition: number;
}

/** Generate unique ID for panels */
const generatePanelId = () => `panel-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Compute derived state from active panels
 */
function computeDerivedState(activePanels: ActiveAppPanel[], visiblePanelId: string | null) {
  const visiblePanel = visiblePanelId
    ? activePanels.find(p => p.id === visiblePanelId)
    : null;

  const hiddenPanels = activePanels.filter(p => p.isHidden);

  return {
    isOpen: visiblePanel !== null && visiblePanel !== undefined,
    route: visiblePanel?.route ?? null,
    title: visiblePanel?.title ?? null,
    width: visiblePanel?.width ?? 'full',
    isTaskbarVisible: hiddenPanels.length > 0,
    minimizedPanels: hiddenPanels.map(p => ({
      id: p.id,
      route: p.route,
      title: p.title,
      width: p.width,
      minimizedAt: p.createdAt,
    })),
  };
}

export const useAppPanelStore = create<AppPanelState>()(
  persist(
    (set, get) => ({
      // Core state
      activePanels: [],
      visiblePanelId: null,
      isFocusMode: false,
      isTaskbarVisible: false,

      // Legacy compatibility (computed)
      isOpen: false,
      route: null,
      title: null,
      width: 'full',
      minimizedPanels: [],
      isMinimized: false,
      pillPosition: 50,

      openPanel: (route, title, width = 'full') => {
        const { activePanels, visiblePanelId } = get();

        // Check if a panel with this route already exists
        const existingPanel = activePanels.find(p => p.route === route);

        if (existingPanel) {
          // Restore existing panel instead of creating new one
          const updatedPanels = activePanels.map(p => ({
            ...p,
            isHidden: p.id === existingPanel.id ? false : true,
          }));

          const derived = computeDerivedState(updatedPanels, existingPanel.id);
          set({
            activePanels: updatedPanels,
            visiblePanelId: existingPanel.id,
            isFocusMode: true,
            ...derived,
          });
          return;
        }

        // Hide currently visible panel if exists
        const updatedPanels = activePanels.map(p => ({
          ...p,
          isHidden: true,
        }));

        // Create new panel
        const newPanel: ActiveAppPanel = {
          id: generatePanelId(),
          route,
          title,
          width,
          isHidden: false,
          createdAt: Date.now(),
        };

        const newActivePanels = [...updatedPanels, newPanel];
        const derived = computeDerivedState(newActivePanels, newPanel.id);

        set({
          activePanels: newActivePanels,
          visiblePanelId: newPanel.id,
          isFocusMode: true,
          ...derived,
        });
      },

      closePanel: () => {
        const { activePanels, visiblePanelId } = get();

        if (!visiblePanelId) return;

        // Remove the visible panel entirely (destroys iframe)
        const updatedPanels = activePanels.filter(p => p.id !== visiblePanelId);
        const derived = computeDerivedState(updatedPanels, null);

        set({
          activePanels: updatedPanels,
          visiblePanelId: null,
          isFocusMode: false,
          ...derived,
        });
      },

      setWidth: (width) => {
        const { activePanels, visiblePanelId } = get();

        if (!visiblePanelId) return;

        const updatedPanels = activePanels.map(p =>
          p.id === visiblePanelId ? { ...p, width } : p
        );

        const derived = computeDerivedState(updatedPanels, visiblePanelId);
        set({
          activePanels: updatedPanels,
          ...derived,
        });
      },

      minimizePanel: () => {
        const { activePanels, visiblePanelId } = get();

        if (!visiblePanelId) return;

        // Mark the visible panel as hidden (iframe stays mounted)
        const updatedPanels = activePanels.map(p =>
          p.id === visiblePanelId ? { ...p, isHidden: true, createdAt: Date.now() } : p
        );

        const derived = computeDerivedState(updatedPanels, null);

        set({
          activePanels: updatedPanels,
          visiblePanelId: null,
          isFocusMode: false,
          ...derived,
        });
      },

      toggleFocusMode: () => {
        set({ isFocusMode: !get().isFocusMode });
      },

      restoreMinimizedPanel: (id: string) => {
        const { activePanels } = get();

        const panelToRestore = activePanels.find(p => p.id === id);
        if (!panelToRestore) return;

        // Hide all panels except the one being restored
        const updatedPanels = activePanels.map(p => ({
          ...p,
          isHidden: p.id !== id,
        }));

        const derived = computeDerivedState(updatedPanels, id);

        set({
          activePanels: updatedPanels,
          visiblePanelId: id,
          isFocusMode: true,
          ...derived,
        });
      },

      closeMinimizedPanel: (id: string) => {
        const { activePanels, visiblePanelId } = get();

        // Remove the panel entirely (destroys iframe)
        const updatedPanels = activePanels.filter(p => p.id !== id);
        const newVisibleId = visiblePanelId === id ? null : visiblePanelId;
        const derived = computeDerivedState(updatedPanels, newVisibleId);

        set({
          activePanels: updatedPanels,
          visiblePanelId: newVisibleId,
          ...derived,
        });
      },

      getMinimizedPanels: () => {
        const { activePanels } = get();
        return activePanels
          .filter(p => p.isHidden)
          .map(p => ({
            id: p.id,
            route: p.route,
            title: p.title,
            width: p.width,
            minimizedAt: p.createdAt,
          }))
          .sort((a, b) => a.minimizedAt - b.minimizedAt);
      },

      // Legacy compatibility methods
      restorePanel: () => {
        const { activePanels } = get();
        const hiddenPanels = activePanels.filter(p => p.isHidden);
        if (hiddenPanels.length === 0) return;

        // Restore the most recently minimized panel
        const lastPanel = hiddenPanels.sort((a, b) => b.createdAt - a.createdAt)[0];
        get().restoreMinimizedPanel(lastPanel.id);
      },

      toggleMinimize: () => {
        const { visiblePanelId, activePanels } = get();
        if (visiblePanelId) {
          get().minimizePanel();
        } else {
          const hiddenPanels = activePanels.filter(p => p.isHidden);
          if (hiddenPanels.length > 0) {
            get().restorePanel();
          }
        }
      },

      setPillPosition: () => {
        // Legacy no-op - pill positioning removed in taskbar redesign
      },
    }),
    {
      name: 'bo360-app-panel-state',
      version: 1,
      partialize: (state) => ({
        // Only persist the active panels array
        // On reload, all panels will be hidden (no visible panel)
        activePanels: state.activePanels.map(p => ({ ...p, isHidden: true })),
      }),
      onRehydrateStorage: () => {
        return (state?: AppPanelState) => {
          if (state) {
            // Recompute derived state after rehydration
            const derived = computeDerivedState(state.activePanels, null);
            Object.assign(state, derived, { visiblePanelId: null, isFocusMode: false });
          }
        };
      },
    }
  )
);

/**
 * Cloud sync data structure for PostgreSQL persistence
 */
interface AppPanelCloudData {
  activePanels: ActiveAppPanel[];
}

/**
 * Cloud sync instance for PostgreSQL persistence
 */
const appPanelCloudSync = createCloudSync<AppPanelCloudData>({
  key: 'app-panel-state',
  namespace: 'ui-panels',
  debounceMs: 1000, // 1 second debounce for cloud sync
});

/**
 * Hook for cloud sync (to be called in providers.tsx)
 * Syncs minimized panels to PostgreSQL for cross-device persistence
 */
export function useAppPanelStoreCloudSync() {
  useEffect(() => {
    const unsubscribe = useAppPanelStore.subscribe((state) => {
      // Only sync hidden panels (minimized) - not currently visible panel
      const panelsToSync = state.activePanels.map(p => ({ ...p, isHidden: true }));

      appPanelCloudSync.syncToCloud({
        activePanels: panelsToSync,
      });
    });

    return () => unsubscribe();
  }, []);
}

/**
 * Initialize from cloud on app startup
 * Merges cloud panels with local panels (cloud takes precedence for conflicts)
 */
export async function initAppPanelFromCloud(): Promise<void> {
  try {
    const cloudData = await appPanelCloudSync.loadFromCloud();

    if (cloudData && cloudData.activePanels && cloudData.activePanels.length > 0) {
      const currentState = useAppPanelStore.getState();
      const localPanels = currentState.activePanels;

      // Merge: cloud panels take precedence, add any local-only panels
      const cloudPanelRoutes = new Set(cloudData.activePanels.map(p => p.route));
      const mergedPanels = [
        ...cloudData.activePanels.map(p => ({ ...p, isHidden: true })),
        ...localPanels.filter(p => !cloudPanelRoutes.has(p.route)),
      ];

      // Update store with merged panels
      const derived = computeDerivedState(mergedPanels, null);
      useAppPanelStore.setState({
        activePanels: mergedPanels,
        visiblePanelId: null,
        isFocusMode: false,
        ...derived,
      });
    }
  } catch (error) {
    console.error('[AppPanelStore] Failed to init from cloud:', error);
  }
}
